/* hooks/useRefinanceCalculator.ts
   ---------------------------------------------------------------
   ClearPath‑accurate payment + interest math
   --------------------------------------------------------------- */

import { useMemo } from "react";
import { useRefinanceContext } from "@/context/RefinanceContext";

/* ── basic helpers ───────────────────────────────────────────── */
const calcPMT = (p: number, i: number, n: number) => (i === 0 || n === 0 ? 0 : (p * i) / (1 - Math.pow(1 + i, -n)));

const monthlyPMI = (principal: number, appraised: number, termYrs: number) => {
    if (!principal || !appraised) return 0;
    const ltv = (principal * 100) / appraised;
    let annual = 0;
    if (ltv > 95) annual = termYrs < 25 ? 0.0099 : 0.011;
    else if (ltv > 90) annual = termYrs < 25 ? 0.0083 : 0.0094;
    else if (ltv > 85) annual = termYrs < 25 ? 0.0051 : 0.0062;
    else if (ltv > 80) annual = termYrs < 25 ? 0.0027 : 0.0038;
    return (principal * annual) / 12;
};

/* cumulative interest over any horizon */
const cumInterest = (principal: number, annualRatePct: number, termMonths: number, horizonMonths: number) => {
    const i = annualRatePct / 100 / 12;
    const pmt = calcPMT(principal, i, termMonths);
    let bal = principal;
    let intSum = 0;
    for (let m = 0; m < horizonMonths; m++) {
        const interest = bal * i;
        intSum += interest;
        bal -= pmt - interest;
    }
    return intSum;
};

/* ───────────────────────────────────────────────────────────── */
type AmortizationEntry = { month: number; principal: number; interest: number; balance: number };

export function useRefinanceCalculator() {
    const { refiLoan, currentLoan, taxesInsurance, homeInfo } = useRefinanceContext();

    /* constant add‑ons */
    const monthlyPropertyTax = (taxesInsurance.annualPropertyTax || 0) / 12;
    const monthlyInsurance = (taxesInsurance.annualInsurance || 0) / 12;

    /* current loan P&I */
    const currentMonthlyPI = useMemo(
        () =>
            calcPMT(
                currentLoan.amount || 0,
                (currentLoan.interestRate || 0) / 100 / 12,
                (currentLoan.termYears || 0) * 12,
            ),
        [currentLoan],
    );

    /* current PMI & full payment */
    const currentPMI = useMemo(
        () => monthlyPMI(currentLoan.amount || 0, homeInfo.appraisedValue || 0, currentLoan.termYears || 0),
        [currentLoan, homeInfo],
    );

    const currentMonthlyPayment = currentMonthlyPI + currentPMI + monthlyPropertyTax + monthlyInsurance;

    /* remaining balance of current loan TODAY (after yearsPaid) */
    const remainingBalanceCurrent = useMemo(() => {
        const P = currentLoan.amount || 0;
        const n = (currentLoan.termYears || 0) * 12;
        const k = (currentLoan.yearsPaid || 0) * 12;
        const i = (currentLoan.interestRate || 0) / 100 / 12;
        if (!P || !n) return 0;
        const pmt = currentMonthlyPI; // we just computed it
        return i === 0 ? P - pmt * k : P * Math.pow(1 + i, k) - (pmt * (Math.pow(1 + i, k) - 1)) / i;
    }, [currentLoan, currentMonthlyPI]);

    /* current interest from now until move‑out */
    const currentInterest = useMemo(() => {
        const horizon = ((currentLoan.yearsPaid || 0) + (homeInfo.expectedYears || 0)) * 12;
        return cumInterest(
            currentLoan.amount || 0,
            currentLoan.interestRate || 0,
            (currentLoan.termYears || 0) * 12,
            horizon,
        );
    }, [currentLoan, homeInfo]);

    /* refi P&I and full payment (no PMI) */
    const refiMonthlyPI = useMemo(
        () => calcPMT(refiLoan.amount || 0, (refiLoan.interestRate || 0) / 100 / 12, (refiLoan.termYears || 0) * 12),
        [refiLoan],
    );

    const refiMonthlyPayment = refiMonthlyPI + monthlyPropertyTax + monthlyInsurance;

    /* refi interest over stay‑horizon, starting from remaining balance */
    const refiInterest = useMemo(() => {
        const horizonMonths = (homeInfo.expectedYears || 0) * 12;
        let bal = remainingBalanceCurrent;
        const i = (refiLoan.interestRate || 0) / 100 / 12;
        const pmt = refiMonthlyPI;
        let totInt = 0;
        for (let m = 0; m < horizonMonths && bal > 0; m++) {
            const intPart = bal * i;
            totInt += intPart;
            bal -= Math.max(pmt - intPart, 0);
        }
        return totInt;
    }, [remainingBalanceCurrent, homeInfo, refiLoan, refiMonthlyPI]);

    /* chart */
    /* breakdown for stacked bar (or pie, if you go back) */
    const breakdownChartData = [
        { name: "Principal & Interest", value: refiMonthlyPI },
        { name: "Taxes & Insurance", value: monthlyPropertyTax + monthlyInsurance },
    ];

    /* comparison bar chart (monthly & interest) */
    const comparisonChartData = [
        {
            label: "Loan Comparison",
            currentLoan: currentMonthlyPayment,
            refiLoan: refiMonthlyPayment,
        },
    ];

    const amortizationData: AmortizationEntry[] = [];

    return {
        /* current */
        currentMonthlyPayment,
        currentInterest,

        /* refi */
        refiMonthlyPayment,
        refiInterest,

        /* extras */
        // chartData,
        amortizationData,
        breakdownChartData,
        comparisonChartData,
    };
}
